import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import SEO from '../components/shared/SEO';
import { getTeam } from '../api';
import '../styles/leadershippage.css';

interface TeamMember {
  _id: string;
  name: string;
  title: string;
  department?: string;
  bio: string;
  photo?: string;
  linkedin?: string;
  twitter?: string;
}

function getInitials(name: string) {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

const memberPhotoMap: Record<string, string> = {
  'Pramod Attarde':   '/members/PramodAttarde.png',
  'Jamil':            '/members/jamil.jpeg',
  'Lalit Shinde':     '/members/lalitShinde.jpeg',
  'Makis':            '/members/makis.jpeg',
  'Sanjay Kamtekar':  '/members/sanjay kamtekar.jpeg',
  'Sunita Attarde':   '/members/sunita attarde.jpeg',
};

function getMemberPhoto(member: TeamMember): string | undefined {
  if (member.photo) return member.photo;
  for (const [key, path] of Object.entries(memberPhotoMap)) {
    if (member.name.toLowerCase().includes(key.toLowerCase())) return path;
  }
  return undefined;
}


export default function LeadershipPage() {
  const { data: members = [], isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: getTeam,
  });

  return (
    <Layout>
      <SEO
        title="Leadership - Apex DeFi Labs Inc."
        description="Meet the visionary leadership team behind Apex DeFi Labs Inc. — decades of experience across technology, finance, and enterprise innovation."
        url="https://apexdefilabs.com/leadership"
        type="website"
        keywords="Apex DeFi Labs Inc. leadership, executive team, DeFi, blockchain"
      />

      <div className="ldp-page">

        {/* ── 1. HERO ────────────────────────────────────────── */}
        <section className="ldp-hero">
          <img
            src="https://apexsign.s3.us-east-2.amazonaws.com/SiteAsset/guide.jpeg"
            alt="Visionary Leadership"
            className="ldp-hero-img"
          />
        </section>

        {/* ── 2. TEAM GRID ───────────────────────────────────── */}
        <section className="ldp-team-section">
          <div className="ldp-shell">
            <div className="ldp-section-header">
              <SectionLabel>Executive Leadership</SectionLabel>
              <h2 className="ldp-section-title">
                The Team Behind <em>The Vision</em>
              </h2>
            </div>
            {isLoading ? (
              <div className="ldp-team-grid">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="ldp-team-card ldp-team-card--loading">
                    <div className="ldp-team-card-top">
                      <div className="ldp-team-avatar ldp-skeleton" />
                      <div className="ldp-skeleton-lines">
                        <div className="ldp-skeleton ldp-skeleton--name" />
                        <div className="ldp-skeleton ldp-skeleton--role" />
                      </div>
                    </div>
                    <div className="ldp-skeleton ldp-skeleton--bio" />
                  </div>
                ))}
              </div>
            ) : members.length === 0 ? (
              <p className="ldp-empty">No team members found.</p>
            ) : (
              <div className="ldp-members-list">
                {members.map((member: TeamMember) => (
                  <div key={member._id} className="ldp-member-card">
                    <div className="ldp-ceo-header">
                      <div className="ldp-ceo-avatar">
                        {getMemberPhoto(member)
                          ? <img src={getMemberPhoto(member)} alt={member.name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          : getInitials(member.name)}
                      </div>
                      <div className="ldp-ceo-identity">
                        <span className="ldp-ceo-role-label">
                          {member.title}{member.department ? ` · ${member.department}` : ''}
                        </span>
                        <h2 className="ldp-ceo-name">{member.name}</h2>
                      </div>
                    </div>
                    {member.bio && (
                      <div className="ldp-ceo-body">
                        <div dangerouslySetInnerHTML={{ __html: member.bio }} />
                      </div>
                    )}
                    {(member.linkedin || member.twitter) && (
                      <div className="ldp-team-socials">
                        {member.linkedin && (
                          <a href={member.linkedin} target="_blank" rel="noreferrer" className="ldp-team-social-link">in</a>
                        )}
                        {member.twitter && (
                          <a href={member.twitter} target="_blank" rel="noreferrer" className="ldp-team-social-link">𝕏</a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── 3. CTA ────────────────────────────────────────── */}
        <section className="ldp-cta-section">
          <div className="ldp-shell">
            <div className="ldp-cta-inner">
              <SectionLabel>Get In Touch</SectionLabel>
              <h2 className="ldp-cta-title">
                Join Us in <em>Shaping the Future</em>
              </h2>
              <p className="ldp-cta-sub">
                Connect with our leadership team to explore partnerships, investment opportunities, and
                innovation initiatives.
              </p>
              <div className="ldp-cta-buttons">
                <Link to="/contact" className="ldp-btn-outline">Partner With Us</Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
}
